import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Plus, Filter, Search, X, Archive, ArchiveRestore, History } from "lucide-react";
import { toast } from "sonner";
import { wapi } from "@/lib/api";

const STATUS = {
  info: { label: "Informação", chip: "border-border bg-secondary/60 text-muted-foreground" },
  pendente: { label: "Pendente", chip: "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300" },
  resolvido: { label: "Resolvido", chip: "border-primary/40 bg-primary/10 text-primary" },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zurich" });

const inputCls =
  "w-full min-h-[48px] px-4 bg-secondary/40 rounded-md border border-border text-base outline-none focus:border-primary transition-colors";

export const MuralBoard = ({ user }) => {
  const [data, setData] = useState({ notes: [], themes: {}, authors: [] });
  const [filters, setFilters] = useState({ theme: "", status: "", author: "", date_from: "", date_to: "", q: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ theme: "", subtheme: "", status: "info", text: "" });
  const [editing, setEditing] = useState(null); // note_id
  const [editText, setEditText] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [histNotes, setHistNotes] = useState([]);
  const [histQ, setHistQ] = useState("");

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    wapi.get("/workers/mural", { params }).then((r) => setData(r.data)).catch(() => toast.error("Não foi possível carregar o mural."));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = useMemo(() => data.notes.filter((n) => n.status === "pendente").length, [data.notes]);
  const subOptions = form.theme ? data.themes[form.theme]?.subs || [] : [];

  const publish = async () => {
    if (!form.theme) return toast.error("Escolha o tema.");
    if (!form.text.trim()) return toast.error("Escreva a informação.");
    setSaving(true);
    try {
      await wapi.post("/workers/mural", form);
      toast.success("Publicado no mural.");
      setForm({ theme: "", subtheme: "", status: "info", text: "" });
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível publicar.");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (note, status) => {
    try {
      await wapi.patch(`/workers/mural/${note.note_id}`, { status });
      toast.success(status === "resolvido" ? "Resolvido e movido para o histórico." : "Reaberto.");
      load();
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const saveEdit = async (note) => {
    try {
      await wapi.patch(`/workers/mural/${note.note_id}`, { text: editText });
      toast.success("Publicação atualizada.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível editar.");
    }
  };

  const remove = async (note) => {
    try {
      await wapi.delete(`/workers/mural/${note.note_id}`);
      toast.success("Registro excluído.");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Não foi possível excluir.");
    }
  };

  const archive = async (note, value = true) => {
    try {
      await wapi.patch(`/workers/mural/${note.note_id}`, { archived: value });
      toast.success(value ? "Nota arquivada no histórico." : "Nota de volta ao mural.");
      load();
      if (histOpen) loadHistory(histQ);
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const loadHistory = useCallback((q = "") => {
    const params = q ? { q } : {};
    wapi.get("/workers/mural/history", { params }).then((r) => setHistNotes(r.data.notes)).catch(() => toast.error("Não foi possível carregar o histórico."));
  }, []);

  const toggleHistory = () => {
    const next = !histOpen;
    setHistOpen(next);
    if (next) loadHistory(histQ);
  };

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <section data-testid="mural-board">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-sans uppercase tracking-[0.22em] text-[11px] font-semibold text-primary flex items-center gap-2">
            <ClipboardList size={14} strokeWidth={1.5} /> Mural dos Trabalhadores
          </p>
          <h2 className="mt-2 font-serif text-2xl md:text-3xl">Recados, informações e pendências da equipe.</h2>
          {pendingCount > 0 && (
            <p className="mt-2 font-sans text-sm text-amber-700 dark:text-amber-300" data-testid="mural-pending-count">
              {pendingCount} {pendingCount === 1 ? "pendência aberta" : "pendências abertas"}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters((v) => !v)}
            data-testid="mural-filter-toggle"
            className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 font-sans text-sm transition-colors ${showFilters || hasFilters ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <Filter size={15} strokeWidth={1.5} /> Filtrar
          </button>
          <button
            onClick={toggleHistory}
            data-testid="mural-history-toggle"
            className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 font-sans text-sm transition-colors ${histOpen ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <History size={15} strokeWidth={1.5} /> Histórico
          </button>
          <button
            onClick={() => setFormOpen((v) => !v)}
            data-testid="mural-new-toggle"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 font-sans text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Plus size={16} strokeWidth={2} /> Nova informação
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3 p-5 rounded-xl border border-border bg-secondary/30" data-testid="mural-filters">
          <select value={filters.theme} onChange={(e) => setFilters({ ...filters, theme: e.target.value })} className={inputCls} data-testid="mural-filter-theme">
            <option value="">Todos os temas</option>
            {Object.entries(data.themes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls} data-testid="mural-filter-status">
            <option value="">Todos os status</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filters.author} onChange={(e) => setFilters({ ...filters, author: e.target.value })} className={inputCls} data-testid="mural-filter-author">
            <option value="">Toda a equipe</option>
            {data.authors.map((a) => <option key={a.email} value={a.email}>{a.name || a.email}</option>)}
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className={inputCls} data-testid="mural-filter-from" aria-label="De" />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className={inputCls} data-testid="mural-filter-to" aria-label="Até" />
          <div className="relative col-span-2 md:col-span-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Buscar no texto…" className={`${inputCls} pl-10`} data-testid="mural-filter-q" />
          </div>
          {hasFilters && (
            <button onClick={() => setFilters({ theme: "", status: "", author: "", date_from: "", date_to: "", q: "" })} className="col-span-2 md:col-span-3 inline-flex items-center gap-1.5 font-sans text-xs uppercase tracking-[0.15em] text-primary" data-testid="mural-filter-clear">
              <X size={12} /> Limpar filtros
            </button>
          )}
        </div>
      )}

      {formOpen && (
        <div className="mt-6 p-5 md:p-6 rounded-xl border border-primary/30 bg-card space-y-4" data-testid="mural-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block font-sans text-sm font-medium mb-1.5">1. Tema</label>
              <select value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value, subtheme: "" })} className={inputCls} data-testid="mural-form-theme">
                <option value="">Escolha…</option>
                {Object.entries(data.themes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-sans text-sm font-medium mb-1.5">2. Subtema <span className="text-muted-foreground font-normal">(opcional)</span></label>
              <select value={form.subtheme} onChange={(e) => setForm({ ...form, subtheme: e.target.value })} className={inputCls} disabled={!form.theme || subOptions.length === 0} data-testid="mural-form-subtheme">
                <option value="">{subOptions.length ? "Escolha…" : "—"}</option>
                {subOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block font-sans text-sm font-medium mb-1.5">3. Status</label>
            <div className="grid grid-cols-3 gap-2" data-testid="mural-form-status">
              {Object.entries(STATUS).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm({ ...form, status: k })}
                  data-testid={`mural-status-${k}`}
                  className={`min-h-[48px] rounded-md border font-sans text-sm transition-colors ${form.status === k ? "border-primary bg-primary text-primary-foreground font-semibold" : "border-border text-muted-foreground hover:border-primary/50"}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-sans text-sm font-medium mb-1.5">4. Informação</label>
            <textarea
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={4}
              placeholder="Escreva aqui a informação para a equipe…"
              className={`${inputCls} py-3 text-base leading-relaxed resize-y`}
              data-testid="mural-form-text"
            />
          </div>
          <button
            onClick={publish}
            disabled={saving}
            data-testid="mural-form-publish"
            className="w-full min-h-[52px] rounded-full bg-primary font-sans text-base font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {saving ? "Publicando…" : "Publicar"}
          </button>
        </div>
      )}

      <ul className="mt-8 space-y-4">
        {data.notes.map((n) => (
          <li key={n.note_id} className="rounded-xl border border-border bg-card p-5" data-testid={`mural-note-${n.note_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-block rounded-full border px-3 py-1 font-sans text-xs font-semibold ${STATUS[n.status]?.chip}`}>
                {STATUS[n.status]?.label}
              </span>
              <span className="font-sans text-xs uppercase tracking-[0.12em] text-muted-foreground">
                {data.themes[n.theme]?.label || n.theme}{n.subtheme ? ` › ${n.subtheme}` : ""}
              </span>
            </div>
            {editing === n.note_id ? (
              <div className="mt-3 space-y-3">
                <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} className={`${inputCls} py-3 resize-y`} data-testid="mural-edit-text" />
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(n)} className="rounded-full bg-primary px-5 py-2.5 font-sans text-sm font-semibold text-primary-foreground" data-testid="mural-edit-save">Salvar</button>
                  <button onClick={() => setEditing(null)} className="rounded-full border border-border px-5 py-2.5 font-sans text-sm text-muted-foreground">Cancelar</button>
                </div>
              </div>
            ) : (
              <p className="mt-3 font-sans text-base md:text-lg leading-relaxed whitespace-pre-line" data-testid="mural-note-text">{n.text}</p>
            )}
            <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
              <p className="font-sans text-sm text-muted-foreground">
                {n.author_name} · {fmtDate(n.created_at)}
                {n.status === "resolvido" && n.resolved_by && <span> · resolvido por {n.resolved_by}</span>}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {n.status === "pendente" && (
                  <button onClick={() => setStatus(n, "resolvido")} className="rounded-full border border-primary/40 px-4 py-2 font-sans text-sm text-primary hover:bg-primary/10 transition-colors" data-testid="mural-resolve">
                    Marcar como resolvido
                  </button>
                )}
                {n.status === "resolvido" && (
                  <button onClick={() => setStatus(n, "pendente")} className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="mural-reopen">
                    Reabrir
                  </button>
                )}
                <button onClick={() => archive(n, true)} className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors" title="Mover para o histórico do mural" data-testid="mural-archive">
                  <Archive size={13} className="inline-block -mt-0.5 mr-1" />Arquivar
                </button>
                {n.author_email === user.email && editing !== n.note_id && (
                  <button onClick={() => { setEditing(n.note_id); setEditText(n.text); }} className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="mural-edit">
                    Editar
                  </button>
                )}
                {(n.author_email === user.email || user.is_admin) && (
                  confirmDel === n.note_id ? (
                    <span className="inline-flex items-center gap-2" data-testid="mural-del-confirm">
                      <span className="font-sans text-sm text-muted-foreground">Excluir mesmo?</span>
                      <button onClick={() => remove(n)} className="rounded-full bg-red-600 px-4 py-2 font-sans text-sm font-semibold text-white" data-testid="mural-del-yes">Sim</button>
                      <button onClick={() => setConfirmDel(null)} className="rounded-full border border-border px-4 py-2 font-sans text-sm">Não</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmDel(n.note_id)} className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-red-600 hover:border-red-300 transition-colors" data-testid="mural-delete">
                      Excluir
                    </button>
                  )
                )}
              </div>
            </div>
          </li>
        ))}
        {data.notes.length === 0 && (
          <li className="py-14 text-center font-sans text-muted-foreground" data-testid="mural-empty">
            Nenhum recado por aqui ainda. Que tal publicar o primeiro?
          </li>
        )}
      </ul>

      {histOpen && (
        <div className="mt-10 rounded-xl border border-border bg-secondary/20 p-5" data-testid="mural-history">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-sans uppercase tracking-[0.18em] text-[11px] font-semibold text-muted-foreground">
              Histórico do mural — resolvidos e arquivados
            </p>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={histQ}
                onChange={(e) => { setHistQ(e.target.value); loadHistory(e.target.value); }}
                placeholder="Buscar no histórico…"
                className="min-h-[40px] pl-9 pr-3 bg-secondary/40 rounded-md border border-border text-sm outline-none focus:border-primary transition-colors"
                data-testid="mural-history-q"
              />
            </div>
          </div>
          <ul className="mt-4 divide-y divide-border/60">
            {histNotes.map((n) => (
              <li key={n.note_id} className="py-4" data-testid={`mural-hist-${n.note_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block rounded-full border px-3 py-1 font-sans text-xs font-semibold ${STATUS[n.status]?.chip}`}>
                    {STATUS[n.status]?.label}
                  </span>
                  <span className="font-sans text-xs uppercase tracking-[0.12em] text-muted-foreground">
                    {data.themes[n.theme]?.label || n.theme}{n.subtheme ? ` › ${n.subtheme}` : ""}
                  </span>
                </div>
                <p className="mt-2 font-sans text-base leading-relaxed whitespace-pre-line text-muted-foreground">{n.text}</p>
                <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                  <p className="font-sans text-xs text-muted-foreground">
                    {n.author_name} · {fmtDate(n.created_at)}
                    {n.resolved_by && <span> · resolvido por {n.resolved_by}</span>}
                    {n.archived_at && <span> · arquivado em {fmtDate(n.archived_at)}</span>}
                  </p>
                  <button
                    onClick={() => archive(n, false)}
                    className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`mural-unarchive-${n.note_id}`}
                  >
                    <ArchiveRestore size={13} className="inline-block -mt-0.5 mr-1" />Trazer de volta
                  </button>
                </div>
              </li>
            ))}
            {histNotes.length === 0 && (
              <li className="py-10 text-center font-sans text-muted-foreground" data-testid="mural-history-empty">
                Nada no histórico ainda.
              </li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
};
